import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useUrlState } from "../hooks/useUrlState";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";

const PAGE_SIZE = 25;

interface RunSummary {
  id: string;
  pipelineId: string;
  jobId: string;
  mergeRequestIid: string | null;
  commitRefSlug: string | null;
  appName: string;
  platform: string;
  status: string;
  totalScenarios: number;
  passedCount: number;
  failedCount: number;
  skippedCount: number;
  deviceCount: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

interface RunsSearchResponse {
  runs: RunSummary[];
  total: number;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "-";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso) return "-";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

/**
 * Extract a human-friendly "who ran this" label from the run's identifiers.
 * CLI runs look like `cli-cdownard-1776974733791` → "cdownard (cli)".
 * GitLab CI runs look like numeric job IDs; we fall back to "ci" for those.
 */
function whoRanIt(run: RunSummary): string {
  const match = run.pipelineId.match(/^cli-([a-zA-Z0-9]+)-\d+$/);
  if (match) return `${match[1]} (cli)`;
  if (run.pipelineId.startsWith("cli-")) return "cli";
  return "ci";
}

export function JobLookup() {
  const { state, setState } = useUrlState({
    page: 1,
    search: "",
  });
  const [input, setInput] = useState(state.search);

  // Re-sync input from URL when the search param changes externally
  // (e.g. browser back/forward navigation). The no-op guard in
  // useUrlState prevents a feedback loop: when the debounce writes the
  // same value to the URL, setState bails out and this effect sees no
  // change.
  useEffect(() => {
    setInput(state.search);
  }, [state.search]);

  // Debounce keystrokes so each keystroke doesn't spam the API.
  // useUrlState auto-resets `page` to 1 when `search` changes.
  // The unchanged-value guard prevents a mount-time fire from
  // clobbering a deep-linked page (e.g. /jobs?search=foo&page=3).
  useEffect(() => {
    const t = setTimeout(() => {
      if (input.trim() !== state.search) {
        setState({ search: input.trim() });
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const params = new URLSearchParams();
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((state.page - 1) * PAGE_SIZE));
  if (state.search) params.set("search", state.search);
  const url = `/api/runs?${params.toString()}`;

  const { data, loading, error } = useApi<RunsSearchResponse>(url);

  // Clamp out-of-range pages once data arrives. Handles /jobs?page=999
  // and the case where a filter shrinks the result set below the
  // current page.
  useEffect(() => {
    if (!data) return;
    const maxPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    if (state.page > maxPage) setState({ page: maxPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleClear = useCallback(() => setInput(""), []);

  return (
    <div>
      <h1
        style={{
          fontSize: "var(--font-size-lg)",
          fontWeight: 600,
          color: "var(--color-primary)",
          marginBottom: "var(--space-md)"
        }}
      >
        Job Lookup
      </h1>
      <p
        style={{
          color: "var(--color-text-secondary)",
          marginBottom: "var(--space-lg)",
          fontSize: "var(--font-size-sm)"
        }}
      >
        Search test runs by CI job ID, pipeline ID, commit ref, or user (e.g. your email prefix).
      </p>

      {/* Search bar */}
      <div
        style={{
          display: "flex",
          gap: "var(--space-sm)",
          marginBottom: "var(--space-lg)",
          position: "relative"
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. cdownard, cli-17769, main, 14050031635..."
          aria-label="Search test runs"
          autoFocus
          style={{
            flex: 1,
            padding: "10px 14px",
            fontSize: "var(--font-size-base)",
            border: "var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-foreground)",
            outline: "none",
            fontFamily: "var(--font-mono)"
          }}
        />
        {input && (
          <button
            onClick={handleClear}
            aria-label="Clear search"
            style={{
              padding: "10px 16px",
              fontSize: "var(--font-size-sm)",
              background: "var(--color-surface)",
              border: "var(--border-default)",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer"
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Result count / status bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--space-md)",
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-sm)"
        }}
      >
        <span>
          {loading && "Searching…"}
          {!loading && data && data.total > 0 && (() => {
            const start = (state.page - 1) * PAGE_SIZE + 1;
            const end = Math.min(state.page * PAGE_SIZE, data.total);
            return `Showing ${start}–${end} of ${data.total}`;
          })()}
          {!loading && data && data.total === 0 && "No results"}
          {!loading && error && <span style={{ color: "var(--color-danger)" }}>Error: {error}</span>}
        </span>
      </div>

      {/* Results list */}
      {data && data.runs.length > 0 && (
        <div
          style={{
            background: "var(--color-surface)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-card)",
            overflow: "hidden"
          }}
        >
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(220px, 2fr) 90px 90px 140px 120px 130px",
              gap: "var(--space-md)",
              padding: "var(--space-sm) var(--space-lg)",
              background: "var(--color-foreground)",
              fontSize: "var(--font-size-xs)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--color-text-secondary)",
              fontWeight: 600
            }}
          >
            <span>Pipeline / Job</span>
            <span>App</span>
            <span>Status</span>
            <span>Results</span>
            <span>Who</span>
            <span>When</span>
          </div>

          {data.runs.map((run) => {
            // Pass rate excludes skipped scenarios (quick-run cache hits and
            // platform-skip). Denominator is passed + failed only.
            const passFailTotal = run.passedCount + run.failedCount;
            const passRate =
              passFailTotal > 0
                ? Math.round((run.passedCount / passFailTotal) * 100)
                : null;
            return (
              <Link
                key={run.id}
                to={`/runs/${run.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(220px, 2fr) 90px 90px 140px 120px 130px",
                  gap: "var(--space-md)",
                  padding: "var(--space-md) var(--space-lg)",
                  borderTop: "1px solid var(--color-foreground)",
                  textDecoration: "none",
                  color: "inherit",
                  alignItems: "center",
                  transition: "background 120ms",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--color-foreground)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "var(--font-size-sm)",
                      fontWeight: 600,
                      fontFamily: "var(--font-mono)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {run.pipelineId}
                  </div>
                  {run.mergeRequestIid && (
                    <div
                      style={{
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-info)",
                        marginTop: 2
                      }}
                    >
                      MR !{run.mergeRequestIid}
                      {run.commitRefSlug ? ` · ${run.commitRefSlug}` : ""}
                    </div>
                  )}
                  {!run.mergeRequestIid && run.commitRefSlug && (
                    <div
                      style={{
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-secondary)",
                        marginTop: 2
                      }}
                    >
                      {run.commitRefSlug}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: "var(--font-size-sm)", textTransform: "capitalize" }}>
                  {run.appName}
                </div>
                <div>
                  <StatusBadge status={run.status} />
                </div>
                <div style={{ fontSize: "var(--font-size-sm)" }}>
                  {run.totalScenarios === 0 ? (
                    <span style={{ color: "var(--color-text-secondary)" }}>—</span>
                  ) : (
                    <>
                      <span style={{ color: "var(--color-success)", fontWeight: 600 }}>
                        {run.passedCount}
                      </span>
                      {" / "}
                      <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>
                        {run.failedCount}
                      </span>
                      {" of "}
                      {run.totalScenarios}
                      {passRate !== null && (
                        <span
                          style={{
                            color: "var(--color-text-secondary)",
                            fontSize: "var(--font-size-xs)",
                            marginLeft: 6
                          }}
                        >
                          ({passRate}%)
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div style={{ fontSize: "var(--font-size-sm)" }}>{whoRanIt(run)}</div>
                <div
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--color-text-secondary)"
                  }}
                >
                  <div>{formatRelative(run.startedAt ?? run.createdAt)}</div>
                  <div style={{ fontSize: "var(--font-size-xs)", marginTop: 2 }}>
                    {formatDuration(run.startedAt, run.finishedAt)}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {data && data.runs.length === 0 && !loading && (
        <div
          style={{
            background: "var(--color-foreground)",
            borderRadius: "var(--radius-md)",
            padding: "var(--space-xl)",
            textAlign: "center",
            color: "var(--color-text-secondary)"
          }}
        >
          {state.search ? (
            <>
              No test runs matching <strong>"{state.search}"</strong>
            </>
          ) : (
            "No test runs yet."
          )}
        </div>
      )}

      {data && (
        <Pagination
          page={state.page}
          pageSize={PAGE_SIZE}
          total={data.total}
          onPageChange={(p) => setState({ page: p })}
        />
      )}
    </div>
  );
}
